// @ts-check

import { createControlComponent } from "@react-leaflet/core";
import L from "leaflet";
import "leaflet-draw";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

class JSXControl extends L.Control {
  onAdd() {
    const div = L.DomUtil.create("div", "leaflet-control");
    L.DomEvent.disableClickPropagation(div);
    L.DomEvent.disableScrollPropagation(div);
    this._container = div;
    return div;
  }

  getContainer() {
    return this._container;
  }
}

const ControlBase = createControlComponent((props) => new JSXControl(props));

/**
 * React component wrapper around a custom Leaflet control.
 * Renders its `children` into the control's DOM container.
 */
export default function CustomControlComponent(
  /** @type {import("react").PropsWithChildren<{ className?: string; disablePropagation?: boolean; style?: React.CSSProperties; position: import("leaflet").ControlPosition }> } */
  { className, style, disablePropagation = true, children, ...rest }
) {
  const ref = useRef(/** @type {JSXControl | null} */ (null));
  const [el, setEl] = useState(/** @type {HTMLElement | null} */ (null));

  useEffect(() => {
    setEl(ref.current?.getContainer() ?? null);
  }, []);

  useEffect(() => {
    if (!el) return;
    el.className = `leaflet-control ${className || ""}`.trim();
    if (style) Object.assign(el.style, style);

    if (disablePropagation) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, [el, className, style, disablePropagation]);

  return (
    <>
      <ControlBase ref={ref} {...rest} />
      {el ? createPortal(children, el) : null}
    </>
  );
}
